import moment from "moment";
import {
    Button,
    Datagrid,
    EditButton,
    ExportButton,
    FunctionField,
    List,
    ShowButton,
    TextField,
    TopToolbar,
    useListContext,
} from "react-admin";
import { useNavigate } from "react-router-dom";
import { Chip } from "@mui/material";
import ControlPointIcon from "@mui/icons-material/ControlPoint";
import FilterListOffIcon from "@mui/icons-material/FilterListOff";
import ReasonSubreasonModuleEnum from "../../enums/ReasonSubreasonModuleEnum";
import { ActionTypeEnum } from "../../enums/ActionTypeEnum";
import CommonFilterCard, { CommonFilterField } from "../Common/CommonFilterCard";
import StyledDatagridWrapper, { commonDatagridSx } from "../Common/StyledDatagridWrapper";

const moduleChoices = Object.entries(ReasonSubreasonModuleEnum).map(([k, v]) => ({
    value: v as string,
    label: k.replaceAll("_", " "),
}));

const actionTypeChoices = Object.entries(ActionTypeEnum).map(([k, v]) => ({
    value: v as string,
    label: k.replaceAll("_", " "),
}));

const clientIdChoices = [
    { value: 2, label: "2 · Centaurus" },
    { value: 4, label: "4 · Customer App" },
    { value: 9, label: "9 · Website" },
];

const renderBooleanChip = (value: boolean | null | undefined, trueLabel = "Yes", falseLabel = "No") => {
    if (value === undefined || value === null) return <span>-</span>;
    return (
        <Chip
            label={value ? trueLabel : falseLabel}
            size="small"
            sx={{
                backgroundColor: value ? "#d4edda" : "#f8d7da",
                color: value ? "#155724" : "#721c24",
                fontWeight: 500,
                height: 22,
            }}
        />
    );
};

const ListActions = () => {
    const { setFilters } = useListContext();
    const navigate = useNavigate();
    const resetFilter = () => {
        setFilters({}, [], false);
    };
    return (
        <TopToolbar>
            <Button
                variant="text"
                size={"small"}
                onClick={resetFilter}
                startIcon={<FilterListOffIcon />}
                label="Clear filters"
            />
            <Button
                variant="text"
                size={"small"}
                onClick={() => navigate("/ReasonSubreason/create")}
                startIcon={<ControlPointIcon />}
                label="Create"
            />
            <ExportButton />
        </TopToolbar>
    );
};

const activeChoices = [
    { value: "true", label: "Yes" },
    { value: "false", label: "No" },
];

const ReasonSubreasonList = () => {
    const filterFields: CommonFilterField[] = [
        { type: "text", label: "Reason", source: "reason" },
        { type: "text", label: "Sub Reason", source: "subReason" },
        { type: "select", label: "Module", source: "module", options: moduleChoices, minWidth: 200 },
        { type: "select", label: "Action Type", source: "actionType", options: actionTypeChoices, minWidth: 220 },
        { type: "select", label: "Client Id", source: "clientId", options: clientIdChoices },
        { type: "select", label: "Active", source: "isActive", options: activeChoices, minWidth: 140 },
        { type: "select", label: "Visible", source: "isVisible", options: activeChoices, minWidth: 140 },
    ];

    return (
        <List
            actions={<ListActions />}
            sort={{ field: "id", order: "DESC" }}
            sx={{
                "& .RaList-content": {
                    boxShadow: "none",
                    backgroundColor: "transparent",
                },
            }}
        >
            <>
            <CommonFilterCard fields={filterFields} />

            <br />

            <StyledDatagridWrapper title="Reason / Sub-reason">
            <Datagrid bulkActionButtons={false} sx={commonDatagridSx}>
                <TextField source="id" />
                <TextField source="reason" />
                <TextField source="subReason" />
                <TextField source="module" />
                <TextField source="actionType" />
                <TextField source="clientId" />
                <FunctionField
                    label="Active"
                    render={(record: any) => renderBooleanChip(record?.isActive)}
                />
                <FunctionField
                    label="Visible"
                    render={(record: any) => renderBooleanChip(record?.isVisible)}
                />
                <FunctionField
                    label="Escalation"
                    render={(record: any) => renderBooleanChip(record?.shouldCreateEscalation)}
                />
                <FunctionField
                    label="Refund %"
                    render={(record: any) =>
                        record?.refundPercentage != null ? `${record.refundPercentage}%` : "-"
                    }
                />
                <FunctionField
                    label="Cook Penalty"
                    render={(record: any) =>
                        record?.cookPenaltyAmount ?? record?.markPenalty ?? "-"
                    }
                />
                <FunctionField
                    label="Created At"
                    render={(record: any) =>
                        record?.createdAt ? moment(record.createdAt).format("DD MMM YYYY, hh:mm A") : "-"
                    }
                />
                <ShowButton />
                <EditButton />
            </Datagrid>
            </StyledDatagridWrapper>
            </>
        </List>
    );
};

export default ReasonSubreasonList;
